/**
 * An error thrown by `ApiClient` (`lib/api.ts`). Declared here rather than
 * beside the client so that the guards below — and their many consumers — do
 * not have to import the client itself just to name its error type; that edge
 * pointed back from `shared/` into `lib/` and closed an import cycle.
 *
 * Branded with `_isApiError` rather than a subclass so the guard survives the
 * structural-clone and cross-realm cases where `instanceof` does not.
 */
export type ApiError = Error & {
	readonly _isApiError: true
	status?: number
	data?: unknown
}

/**
 * Type guard to check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
	return error instanceof Error && '_isApiError' in error
}

/**
 * Type guard to check if an error is an abort error
 */
export function isAbortError(error: unknown): boolean {
	if (error instanceof Error) {
		if (error.name === 'AbortError') return true
		const message = error.message.toLowerCase()
		if (
			message.includes('abort') ||
			message.includes('signal is aborted') ||
			message.includes('request was cancelled')
		) {
			return true
		}
	}

	if (error instanceof DOMException && error.code === DOMException.ABORT_ERR) {
		return true
	}

	return false
}

/**
 * Network error = ApiError without status (fetch failed)
 */
export function isNetworkError(error: unknown): boolean {
	return isApiError(error) && error.status === undefined
}

/**
 * Timeout error
 */
export function isTimeoutError(error: unknown): boolean {
	return error instanceof Error && error.name === 'TimeoutError'
}

/**
 * Contract error = the response arrived intact but did not match the schema the
 * client was built against (backend drift, or a payload from an older contract).
 *
 * Detected by brand rather than by class so this module stays decoupled from
 * whichever feature owns the schema — `shared/bql` throws the first of these.
 *
 * The distinction matters for `smartRetry`: a contract error is *not* an
 * `ApiError`, so without this guard it falls through to that function's trailing
 * `!isApiError(error)` and gets retried three times with backoff — against a
 * server that will deterministically return the very same invalid body.
 */
export function isContractError(error: unknown): error is ContractError {
	return error instanceof Error && '_isContractError' in error
}

/**
 * The brand a contract error carries. Owners subclass `Error` and add whatever
 * detail their boundary needs (the failing route, the underlying `ZodError`);
 * only the brand is shared, so this module stays agnostic about that detail.
 */
export type ContractError = Error & { readonly _isContractError: true }

/**
 * Auth error (401/403)
 */
export function isAuthError(error: unknown): boolean {
	return isApiError(error) && (error.status === 401 || error.status === 403)
}

/**
 * Forbidden error (403)
 */
export function isForbiddenError(error: unknown): boolean {
	return isApiError(error) && error.status === 403
}

/**
 * Not found error (404)
 */
export function isNotFoundError(error: unknown): boolean {
	return isApiError(error) && error.status === 404
}

/**
 * Server error (5xx)
 */
export function isServerError(error: unknown): boolean {
	return isApiError(error) && error.status !== undefined && error.status >= 500
}

/**
 * Get a user-friendly error message from an API error
 * @param error - The error
 */
export function getErrorMessage(error: unknown): string {
	if (!isApiError(error)) {
		return error instanceof Error
			? error.message
			: 'An unexpected error occurred'
	}

	// Check if API returned a specific error message
	if (error.data && typeof error.data === 'object' && 'message' in error.data) {
		return String(error.data.message)
	}

	// Provide friendly messages based on status code
	switch (error.status) {
		case 401:
			return 'You are not authenticated. Please sign in.'
		case 403:
			return 'You do not have permission to perform this action.'
		case 404:
			return 'The requested resource was not found.'
		case 422:
			return 'The data provided is invalid. Please check your input.'
		case 429:
			return 'Too many requests. Please try again later.'
		case 500:
			return 'A server error occurred. Please try again later.'
		case 503:
			return 'The service is temporarily unavailable. Please try again later.'
		default:
			return error.message || 'An error occurred while processing your request.'
	}
}
